import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../api";
import { COLOR, FONT, RADIUS, EASE, EASE_SPRING } from "./mobiletheme";

const CATEGORY_PATHS = {
  garland: "/m-garland",
  courier: "/courier",
  vegetables: "/category/vegetables",
  flowers: "/category/flowers",
  accessories: "/category/mobile-accessories",
};

const CATEGORY_ALIASES = {
  "mobile accessories": "accessories",
  "mobile-accessories": "accessories",
  "mobile_accessories": "accessories",
  "flower garland": "garland",
  "1-day courier": "courier",
  "1 day courier": "courier",
  "vegitable": "vegetables",
  "vegitables": "vegetables",
  "vegetable": "vegetables",
  "flower": "flowers",
};

export const resolveCategoryPath = (rawCategory) => {
  if (!rawCategory) return null;
  const normalized = String(rawCategory).trim().toLowerCase();
  const key = CATEGORY_ALIASES[normalized] || normalized;
  return CATEGORY_PATHS[key] || null;
};

// Ad images can come back from the API as relative paths (e.g. "/uploads/ad1.jpg").
// Those need the API host prefixed on, or the browser resolves them against the
// frontend's own origin and the request 404s (showing as a broken image).
const resolveImageUrl = (rawImage) => {
  if (!rawImage) return "";
  const url = String(rawImage).trim();
  if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Different ad endpoints/tables have been seen returning the picture under
// different keys (image, image_url, banner, img). Check them all instead of
// assuming "image" — a mismatch here is why one slider's images can go
// missing while another slider (reading the same field name successfully)
// still works.
const getAdImageField = (ad) => ad.image || ad.image_url || ad.banner || ad.banner_image || ad.img || ad.photo || "";

const SWIPE_THRESHOLD = 40;

const AdsSlider = ({ autoPlay = true, apiEndpoint = "/ads", customAction, showDots = true, background = "transparent" }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});

  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAds = async () => {
      try {
        const res = await axios.get(`${API_URL}${apiEndpoint}`);
        if (!cancelled && res.data.success) setAds(res.data.ads);
      } catch (err) {
        console.log(err);
      }
    };
    fetchAds();
    return () => { cancelled = true; };
  }, [apiEndpoint]);

  useEffect(() => {
    if (!autoPlay || ads.length <= 1 || isDragging) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === ads.length - 1 ? 0 : prev + 1));
    }, 3500);
    return () => clearInterval(interval);
  }, [ads, autoPlay, isDragging]);

  if (ads.length === 0) return null;

  const handleActionClick = (ad) => {
    if (customAction) {
      customAction(ad, navigate);
    } else {
      const path = resolveCategoryPath(ad.category) || resolveCategoryPath(ad.title) || resolveCategoryPath(ad.name);
      if (path) {
        navigate(path);
      } else {
        console.warn(`[AdsSlider] "Order Now" tapped but ad.category/title didn't match.`);
      }
    }
  };

  const handleTouchStart = (e) => {
    if (ads.length <= 1) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || ads.length <= 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    if (isHorizontalSwipe.current === null) {
      if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (isHorizontalSwipe.current) {
      if (e.cancelable) e.preventDefault();
      setDragOffset(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    if (isHorizontalSwipe.current) {
      if (dragOffset <= -SWIPE_THRESHOLD) {
        setCurrentIndex((prev) => (prev === ads.length - 1 ? 0 : prev + 1));
      } else if (dragOffset >= SWIPE_THRESHOLD) {
        setCurrentIndex((prev) => (prev === 0 ? ads.length - 1 : prev - 1));
      }
    }

    setIsDragging(false);
    setDragOffset(0);
    isHorizontalSwipe.current = null;
  };

  const trackTransform = isDragging
    ? `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))`
    : `translateX(-${currentIndex * 100}%)`;

  return (
    <div style={{ ...styles.wrapper, backgroundColor: background }}>
      <style>{`
        .sb-ad-order-btn { transition: transform 0.16s ${EASE_SPRING}, box-shadow 0.16s ${EASE}; }
        .sb-ad-order-btn:active { transform: scale(0.92); }
        .sb-ad-rail-seg:active { transform: scale(0.85); }
      `}</style>
      <div
        style={{
          ...styles.track,
          transform: trackTransform,
          transition: isDragging ? "none" : `transform 0.55s ${EASE}`,
          touchAction: "pan-y",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {ads.map((ad, i) => {
          const adKey = ad.id ?? i;
          const imageFailed = brokenImages[adKey];
          const resolvedSrc = resolveImageUrl(getAdImageField(ad));
          if (!resolvedSrc && process.env.NODE_ENV !== "production") {
            console.warn("[AdsSlider] no usable image field found on ad:", ad);
          }
          return (
          <div key={adKey} style={styles.slide}>
            <div style={styles.card}>
              {!imageFailed && resolvedSrc ? (
                <img
                  src={resolvedSrc}
                  alt={ad.title || t("mobile.orderNow")}
                  style={styles.image}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onError={() => setBrokenImages((prev) => ({ ...prev, [adKey]: true }))}
                />
              ) : (
                <div style={styles.imageFallback}>
                  <span style={styles.imageFallbackText}>{ad.title || t("mobile.orderNow")}</span>
                </div>
              )}

              <div style={styles.scrim} />

              <div style={styles.captionWrap}>
                <div style={styles.captionTextCol}>
                  {ad.title && (
                    <>
                      <span style={styles.captionEyebrow}> {t("mobile.featured")} </span>
                      <span style={styles.captionText}>{ad.title}</span>
                    </>
                  )}
                  <button
                    type="button"
                    className="sb-tap sb-ad-order-btn"
                    style={styles.orderBtn}
                    onClick={() => handleActionClick(ad)}
                  >
                    {t("mobile.orderNow")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {showDots && ads.length > 1 && (
        <div style={styles.railTrack}>
          {ads.map((_, index) => (
            <div
              key={index}
              onClick={() => setCurrentIndex(index)}
              className="sb-tap sb-ad-rail-seg"
              style={index === currentIndex ? styles.railSegActive : styles.railSeg}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsSlider;

const styles = {
  wrapper: {
    width: "100%",
    aspectRatio: "10 / 6",
    maxHeight: "280px",
    overflow: "hidden",
    position: "relative",
    boxSizing: "border-box",
    marginTop: "-1px",
    paddingBottom: "20px",
    WebkitFontSmoothing: "antialiased",
  },
  track: {
    display: "flex",
    width: "100%",
    height: "100%",
    overscrollBehavior: "contain",
    WebkitUserSelect: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
  },
  slide: { minWidth: "100%", height: "100%", boxSizing: "border-box", display: "flex", justifyContent: "center", alignItems: "center", padding: "18px 18px 0" },
  card: { position: "relative", width: "100%", height: "100%", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", background: COLOR.surface },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transformOrigin: "center",
    fontFamily: FONT.body,
    fontWeight: 400,
    color: COLOR.muted,
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    userSelect: "none",
    pointerEvents: "none",
  },
  imageFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: COLOR.surface,
  },
  imageFallbackText: {
    fontFamily: FONT.body,
    fontWeight: 400,
    fontSize: "clamp(12px, 3.5vw, 14px)",
    color: COLOR.muted,
  },
  scrim: { position: "absolute", left: 0, right: 0, bottom: 0, height: "48%", background: "var(--scrim)", pointerEvents: "none" },
  captionWrap: { position: "absolute", left: "14px", right: "60px", bottom: "12px", display: "flex", flexDirection: "row", alignItems: "flex-end", justifyContent: "space-between", gap: "10px" },
  captionTextCol: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px", minWidth: 0, flex: 1 },
  orderBtn: {
    flexShrink: 0,
    fontFamily: FONT.body,
    fontSize: "clamp(10px, 3vw, 12px)",
    fontWeight: 700,
    color: "#000000",
    background: "#ffffff",
    border: "none",
    padding: "8px 14px",
    borderRadius: "10px",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
    marginBottom: "10px",
    marginLeft: "8px",
  },
  captionEyebrow: { fontFamily: FONT.body, fontSize: "clamp(9px, 2.5vw, 10px)", fontWeight: 700, color: "var(--pop)" },
  captionText: { fontFamily: FONT.display, fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 700, color: "var(--onPrimary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  railTrack: { position: "absolute", bottom: "0", height: "20px", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", gap: "5px" },
  railSegActive: {
    width: "22px",
    height: "4px",
    borderRadius: RADIUS.pill,
    backgroundColor: "var(--paper)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    transition: `width 0.32s ${EASE_SPRING}, background-color 0.32s ${EASE}`,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  },
  railSeg: {
    width: "9px",
    height: "4px",
    borderRadius: RADIUS.pill,
    backgroundColor: "rgba(255, 255, 255, 0.45)",
    transition: `width 0.32s ${EASE_SPRING}, background-color 0.32s ${EASE}`,
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  },
};